import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { ViewFinder, SpinnerWrapper, Spinner } from './QrScanner.styles';

interface QrScannerProps {
  onCapture: (code: string) => void;
}

const aspectRatio = 1.0;
const viewFinderConfig = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio };

const cameraState = {
  UNKNOWN: 0,
  NOT_STARTED: 1,
  SCANNING: 2,
  PAUSED: 3,
};

const Loader = () => (
  <SpinnerWrapper>
    <Spinner />
  </SpinnerWrapper>
);

const QrScanner = ({ onCapture }: QrScannerProps) => {
  const [cameraReady, setCameraReady] = useState(false);
  const viewFinderRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<Html5Qrcode | null>(null);
  const shouldStopRef = useRef<boolean>(false);

  useEffect(() => {
    shouldStopRef.current = false;

    if (viewFinderRef.current && !cameraRef.current) {
      cameraRef.current = new Html5Qrcode(viewFinderRef.current.id);

      const handleOnCapture = (decodedText: string) => {
        onCapture(decodedText);
        cameraRef.current?.stop();
      };

      const startCamera = async () => {
        await cameraRef.current?.start(
          { facingMode: 'environment' },
          viewFinderConfig,
          handleOnCapture,
          undefined
        );

        if (shouldStopRef.current) {
          cameraRef.current?.stop();
        } else {
          setCameraReady(true);
        }
      };

      startCamera();
    }

    return () => {
      if (cameraRef.current?.getState() === cameraState.SCANNING) {
        cameraRef.current?.stop();
      } else {
        shouldStopRef.current = true;
      }
    };
  }, [onCapture]);

  return (
    <>
      {!cameraReady && <Loader />}
      <ViewFinder id="ViewFinder" ref={viewFinderRef} aria-label="View Finder" />
    </>
  );
};

export default QrScanner;
